import { Node, mergeAttributes } from "@tiptap/core";
import { ReactNodeViewRenderer, NodeViewWrapper, NodeViewProps } from "@tiptap/react";
import PlyrYoutubeRenderer from "../PlyrYoutubeRenderer";
import { cn } from "@/lib/utils";
import { useIsMobile } from "@/hooks/use-mobile";

// Extract YouTube video ID from various URL formats
function extractVideoId(url: string): string | null {
  if (!url) return null;
  
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\s?]+)/,
    /^([a-zA-Z0-9_-]{11})$/,
  ];
  
  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match) return match[1];
  }
  
  return null;
}

/**
 * Translate desktop media layout to mobile layout:
 * - Breakout (media-default) → Full width (media-full-width)
 * - Full width, Inline → unchanged
 */
function getMobileLayoutClass(desktopClass: string): string {
  if (desktopClass.includes("media-default")) {
    return desktopClass.replace("media-default", "media-full-width");
  }
  return desktopClass;
}

// Read-only NodeView for blog rendering
function PlyrYoutubeNodeView({ node }: NodeViewProps) {
  const isMobile = useIsMobile();
  const src = node.attrs.src || "";
  let layoutClass = node.attrs.class || "media-default";
  const videoId = extractVideoId(src);
  
  // Apply mobile layout translation
  if (isMobile) {
    layoutClass = getMobileLayoutClass(layoutClass);
  }
  
  // Determine if full-width - no border radius
  const isFullWidth = layoutClass.includes("media-full-width");

  return (
    <NodeViewWrapper className={cn("plyr-youtube-wrapper", layoutClass)}>
      <PlyrYoutubeRenderer videoId={videoId || ""} isFullWidth={isFullWidth} />
    </NodeViewWrapper>
  );
}

export interface PlyrYoutubeNodeOptions {
  HTMLAttributes: Record<string, any>;
}

export const PlyrYoutubeNode = Node.create<PlyrYoutubeNodeOptions>({
  name: "youtube",
  
  group: "block",
  
  atom: true,
  
  draggable: false,
  
  selectable: false,

  addOptions() {
    return {
      HTMLAttributes: {},
    };
  },

  addAttributes() {
    return {
      src: {
        default: null,
        parseHTML: (element) => {
          const iframe = element.querySelector("iframe");
          if (iframe) {
            return iframe.getAttribute("src");
          }
          return element.getAttribute("data-src");
        },
      },
      width: {
        default: 640,
      },
      height: {
        default: 360,
      },
      class: {
        default: "media-default",
        parseHTML: (element) => element.getAttribute("class") || "media-default",
        renderHTML: (attributes) => {
          return { class: attributes.class };
        },
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: "div[data-youtube-video]",
      },
      {
        tag: "figure[data-youtube-video]",
      },
      {
        tag: "div[data-plyr-provider=\"youtube\"]",
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    const videoId = extractVideoId(HTMLAttributes.src || "");
    const layoutClass = HTMLAttributes.class || "media-default";
    const isFullWidth = layoutClass.includes("media-full-width");
    const radiusClass = isFullWidth ? "" : "rounded-3xl";
    
    return [
      "figure",
      mergeAttributes(this.options.HTMLAttributes, HTMLAttributes, {
        "data-youtube-video": "",
        class: layoutClass,
      }),
      [
        "div",
        { class: cn("plyr-container overflow-hidden", radiusClass) },
        [
          "div",
          {
            "data-plyr-provider": "youtube",
            "data-plyr-embed-id": videoId,
            class: radiusClass,
          },
        ],
      ],
    ];
  },

  addNodeView() {
    return ReactNodeViewRenderer(PlyrYoutubeNodeView);
  },
});

export default PlyrYoutubeNode;
